// Client requests, the client's own side (PRD 63 §6).
//
// PLAIN SESSION CALLS, NOT `businessApi*`. Those helpers throw before the
// request leaves the browser when no business is selected, which made this
// whole screen unreachable for a client whose only account is a personal one —
// and a client is a PERSON, who may be asked for a W-9 without keeping a
// single ledger here.

import { apiGet, apiPost, apiPostForm } from '../utils/apiUtils';
import { parseData } from './businessApiCommon';
import {
  ClientRequestDetail,
  ClientRequestSummary,
  ClientRequestsSummary,
  RespondInput,
} from '../types/clientRequestApi';

const BASE = '/api/v1/client-requests';

export const clientRequestService = {
  /**
   * What the firm is still waiting on — or, with `includeClosed`, everything
   * the client was ever sent. The default is the queue because that is what
   * the dashboard card and the badge count; the history is for the screen
   * where the client reads back what they told their accountant.
   *
   * An API that predates the flag ignores it and answers with the queue, so a
   * caller asking for history must cope with getting none.
   */
  async listRequests(options: { includeClosed?: boolean } = {}): Promise<ClientRequestSummary[]> {
    const response = await apiGet(options.includeClosed ? `${BASE}?include_closed=true` : BASE);
    return parseData<ClientRequestSummary[]>(response);
  },

  /**
   * Readable even once closed, so a client who had the page open when the firm
   * withdrew it is told so rather than shown a 404.
   */
  async getRequest(id: string): Promise<ClientRequestDetail> {
    const response = await apiGet(`${BASE}/${id}`);
    return parseData<ClientRequestDetail>(response);
  },

  /**
   * The sidebar's question, asked once per sign-in. Deliberately not the index:
   * drawing one row must not cost a list of every request.
   */
  async getSummary(): Promise<ClientRequestsSummary> {
    const response = await apiGet(`${BASE}/summary`);
    return parseData<ClientRequestsSummary>(response);
  },

  /**
   * ONE ENDPOINT FOR EVERY KIND, typed by the item. A route per kind would put
   * the vocabulary in the URL, and the kinds still to come would break every
   * client that had learned the old list.
   */
  async respond(
    requestId: string,
    itemId: string,
    input: RespondInput,
  ): Promise<ClientRequestDetail> {
    const response = await apiPost(`${BASE}/${requestId}/items/${itemId}/respond`, input);
    return parseData<ClientRequestDetail>(response);
  },

  /**
   * The same endpoint, as multipart. A client WITH a seat could not upload
   * anything here until Wave 5 while somebody holding a link could, which
   * inverts §4.1: the tokenised page is the fallback, not the better one.
   */
  async upload(requestId: string, itemId: string, files: File[]): Promise<ClientRequestDetail> {
    const form = new FormData();
    files.forEach(file => form.append('files[]', file));
    // NO Content-Type: the browser has to append the multipart boundary, and
    // setting it here makes the body unparseable server-side.
    const response = await apiPostForm(`${BASE}/${requestId}/items/${itemId}/respond`, form);
    return parseData<ClientRequestDetail>(response);
  },
};
